// src/pages/Laboratorio/Acompanhamento/DetalhesCaseTimeline.tsx
import React from "react";
import { Navigate, useParams } from "react-router-dom";
import TitleButtons from "../../../components/TitleButtons";

// ====================== Tipos do Acompanhamento ======================
type AcompHistoricoItem = {
  id: string | number;
  status_id: number | string;
  status_nomenclatura?: string | null; // ✅ NOVO (vem da API)
  at: string;
  usuario_id?: number | string | null;
  usuario_nome: string | null;
  comentario: string | null;
};

type AcompReparo = {
  id: string;
  public_token: string;
  equipamento_nome: string | null;
  case_ref: string | null;
  descricao_problema: string | null;
  prioridade: number | null;

  // ✅ empresa cliente
  empresa_cliente_id: string | null;
  empresa_cliente_razao_social: string | null;

  // ✅ solicitante
  solicitante_id?: string | number | null;
  solicitante_nome?: string | null;

  embarcacao_cliente_id: string | null;
  status_atual_id: string | number | null;
  historico: AcompHistoricoItem[];
};

type AcompResponse = {
  fonte: string;
  reparo: AcompReparo;
  erro?: string;
};

type StatusOption = {
  id: number;
  nome: string;
  ativo: boolean;
  visivel_cliente: boolean;
  ordem_sugerida: number | null;
  created_at: string;
  empresa_id: number;
};

type StatusOptionsResponse = {
  fonte?: string;
  empresa_id?: number;
  itens?: StatusOption[];
  erro?: string;
};

// ====================== UI Auxiliar ======================
const NoticeBanner: React.FC<{
  type: "success" | "error" | "notify";
  children: React.ReactNode;
}> = ({ type, children }) => {
  let bg = "#e7f1ff",
    color = "#084298",
    border = "#b6d4fe";
  if (type === "success") {
    bg = "#e6ffed";
    color = "#0f5132";
    border = "#badbcc";
  }
  if (type === "error") {
    bg = "#ffecec";
    color = "#842029";
    border = "#f5c2c7";
  }
  return (
    <div
      role="alert"
      style={{
        margin: "12px 40px 0 40px",
        padding: "10px 12px",
        borderRadius: 0,
        background: bg,
        color,
        border: `1px solid ${border}`,
      }}
    >
      {children}
    </div>
  );
};

const Field: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div style={{ marginBottom: 24 }}>
    <div
      style={{
        fontFamily: "Inter",
        fontWeight: 600,
        fontSize: 15,
        marginBottom: 4,
      }}
    >
      {label}
    </div>
    <div style={{ fontFamily: "Inter", fontWeight: 300, fontSize: 14 }}>
      {value}
    </div>
  </div>
);

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #e8e8e8",
      borderRadius: 0,
      padding: 16,
    }}
  >
    <div
      style={{
        fontFamily: "Inter",
        fontWeight: 800,
        fontSize: 13,
        opacity: 0.75,
        marginBottom: 10,
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

// ====================== Helpers ======================
function safeText(v: any) {
  const s = v === null || v === undefined ? "" : String(v).trim();
  return s === "" ? "—" : s;
}

function formatDateTimeBR(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso || "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} - ${hh}:${mi}`;
}

function dedupeHistorico(items: AcompHistoricoItem[]) {
  const seen = new Set<string>();
  const out: AcompHistoricoItem[] = [];
  for (const it of items || []) {
    const key = String((it as any).id ?? `${it.status_id}|${it.at}`);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(it);
  }
  return out;
}

function sortHistoricoDesc(items: AcompHistoricoItem[]) {
  const copy = [...(items || [])];
  copy.sort((a, b) => {
    const ta = a.at ? Date.parse(a.at) : 0;
    const tb = b.at ? Date.parse(b.at) : 0;
    return tb - ta;
  });
  return copy;
}

function toneByName(name?: string | null): {
  dotBg: string;
  dotBd: string;
  dotFg: string;
  title: string;
} {
  const n = String(name ?? "").toLowerCase();
  if (n.includes("final") || n.includes("conclu") || n.includes("entreg")) {
    return {
      dotBg: "#e6ffed",
      dotBd: "#badbcc",
      dotFg: "#0f5132",
      title: "#0f5132",
    };
  }
  if (n.includes("cancel") || n.includes("reprov")) {
    return {
      dotBg: "#ffecec",
      dotBd: "#f5c2c7",
      dotFg: "#842029",
      title: "#842029",
    };
  }
  if (n.includes("aguard") || n.includes("pend") || n.includes("peça")) {
    return {
      dotBg: "#fff7e6",
      dotBd: "#ffe2ad",
      dotFg: "#7a4a00",
      title: "#7a4a00",
    };
  }
  return {
    dotBg: "#e7f1ff",
    dotBd: "#b6d4fe",
    dotFg: "#084298",
    title: "#084298",
  };
}

// ✅ NOVO: resolve o nome do status, priorizando a nomenclatura que vem no histórico
function resolveStatusNome(
  h: { status_nomenclatura?: string | null; status_id?: any } | null,
  statusNameById: (id: any) => string
) {
  const nom = safeText(h?.status_nomenclatura);
  if (nom !== "—") return nom;
  return statusNameById(h?.status_id);
}

// ====================== API ======================
async function getAcompanhamentoByCase(publicToken: string) {
  const url = `https://api.eva-googlemarine.com/departamentos/laboratorio/acompanhamento/get-status-reparos-id?public_token=${encodeURIComponent(
    publicToken
  )}`;

  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });

  const data = (await res.json().catch(() => null)) as AcompResponse | null;

  if (!res.ok) {
    const msg = (data as any)?.erro || `Erro HTTP ${res.status}`;
    throw new Error(msg);
  }
  if (!data?.reparo) throw new Error("Resposta inválida da API (sem reparo).");
  return data;
}

// ====================== Timeline ======================
type TimelineItem = {
  key: string;
  title: string;
  subtitle?: string;
  dateText: string;
  description?: string;
  isCurrent: boolean;
};

const Timeline: React.FC<{ items: TimelineItem[] }> = ({ items }) => {
  return (
    <div style={{ padding: "12px 0 0 0" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "40px 1fr",
          columnGap: 14,
        }}
      >
        <div style={{ position: "relative" }}>
          <div
            style={{
              position: "absolute",
              left: 19,
              top: 10,
              bottom: 10,
              width: 2,
              background: "#e8e8e8",
            }}
          />
        </div>

        <div>
          {items.map((it, idx) => {
            const t = toneByName(it.title);
            const dot = it.isCurrent
              ? {
                  dotBg: "#124280",
                  dotBd: "#124280",
                  dotFg: "#fff",
                  title: "#124280",
                }
              : t;

            return (
              <div
                key={it.key}
                style={{
                  display: "grid",
                  gridTemplateColumns: "40px 1fr",
                  columnGap: 14,
                  padding: "12px 0",
                }}
              >
                <div style={{ position: "relative" }}>
                  <div
                    style={{
                      position: "absolute",
                      left: 10,
                      top: 2,
                      height: 18,
                      width: 18,
                      borderRadius: 999,
                      background: dot.dotBg,
                      border: `2px solid ${dot.dotBd}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: "Inter",
                      fontSize: 11,
                      fontWeight: 800,
                      color: dot.dotFg,
                      boxSizing: "border-box",
                    }}
                    title={it.isCurrent ? "Status atual" : "Histórico"}
                  >
                    {it.isCurrent ? "✓" : ""}
                  </div>
                </div>

                <div
                  style={{
                    borderBottom:
                      idx === items.length - 1 ? "none" : "1px solid #f0f0f0",
                    paddingBottom: 12,
                  }}
                >
                  <div
                    style={{
                      fontFamily: "Inter",
                      fontWeight: 800,
                      fontSize: 14,
                      color: dot.title,
                      marginBottom: 4,
                    }}
                  >
                    {it.title}
                    {it.isCurrent ? (
                      <span
                        style={{
                          marginLeft: 10,
                          fontSize: 11,
                          fontWeight: 800,
                          padding: "3px 8px",
                          borderRadius: 999,
                          border: "1px solid #b6d4fe",
                          background: "#e7f1ff",
                          color: "#084298",
                          verticalAlign: "middle",
                        }}
                      >
                        ATUAL
                      </span>
                    ) : null}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 10,
                      alignItems: "baseline",
                      fontFamily: "Inter",
                      fontSize: 12,
                      opacity: 0.85,
                      marginBottom: it.description ? 6 : 0,
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>{it.dateText}</span>
                    {it.subtitle ? <span>• {it.subtitle}</span> : null}
                  </div>

                  {it.description ? (
                    <div
                      style={{
                        fontFamily: "Inter",
                        fontSize: 13,
                        opacity: 0.9,
                        lineHeight: 1.35,
                      }}
                    >
                      {it.description}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ====================== Página ======================
const LaboratorioDetalhesCaseTimeline: React.FC = () => {
  const { codigo: codigoParam } = useParams<{ codigo: string }>();

  // ✅ agora esse "codigo" é o public_token vindo da rota
  const publicToken = decodeURIComponent(codigoParam ?? "");

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [data, setData] = React.useState<AcompResponse | null>(null);

  // (mantido, mas aqui você pode nem precisar mais se a API já manda a nomenclatura)
  const [statusOptions, setStatusOptions] = React.useState<StatusOption[]>([]);
  const [statusOptError, setStatusOptError] = React.useState<string | null>(
    null
  );

  // ✅ fallback por id (se por algum motivo status_nomenclatura vier null)
  const statusNameById = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const o of statusOptions || []) {
      const k = String(Number((o as any).id));
      m.set(k, o.nome);
    }
    return (id: number | string | null | undefined) => {
      if (id === null || id === undefined) return "—";
      const k = String(Number(id));
      return m.get(k) ?? `Status ${k}`;
    };
  }, [statusOptions]);

  const reparo = data?.reparo ?? null;

  const timelineItems: TimelineItem[] = React.useMemo(() => {
    if (!reparo) return [];

    const hist = sortHistoricoDesc(dedupeHistorico(reparo.historico || []));
    const currentId = String(Number(reparo.status_atual_id ?? 0));

    const mapped = hist.map((h, idx) => {
      // ✅ AGORA: usa a nomenclatura do próprio histórico
      const title = resolveStatusNome(h, statusNameById);

      const user = h.usuario_nome ? String(h.usuario_nome) : "—";
      const comment = safeText(h.comentario);
      const histId = String(Number(h.status_id));

      const isCurrent = idx === 0 && histId === currentId;

      return {
        key: `hist_${String((h as any).id ?? `${h.status_id}_${h.at}`)}`,
        title,
        subtitle:
          user === "—"
            ? isCurrent
              ? "Status atual do case"
              : undefined
            : `por ${user}`,
        dateText: formatDateTimeBR(h.at),
        description: comment === "—" ? undefined : comment,
        isCurrent,
      } as TimelineItem;
    });

    if (mapped.length === 0) {
      return [
        {
          key: `current_${currentId}`,
          // ✅ se quiser, mantém fallback por id
          title: statusNameById(reparo.status_atual_id ?? null),
          subtitle: "Status atual do case",
          dateText: "—",
          isCurrent: true,
        },
      ];
    }

    const topHistId = String(Number(hist[0]?.status_id ?? -1));
    if (topHistId !== currentId) {
      return [
        {
          key: `current_${currentId}`,
          title: statusNameById(reparo.status_atual_id ?? null),
          subtitle: "Status atual do case",
          dateText: "—",
          isCurrent: true,
        },
        ...mapped,
      ];
    }

    return mapped;
  }, [reparo, statusNameById]);

  // ✅ FUNÇÃO DE FETCH (reutilizável no polling)
  const fetchCase = React.useCallback(async () => {
    if (!publicToken) return;
    const resp = await getAcompanhamentoByCase(publicToken);
    setData(resp);
    setError(null);
  }, [publicToken]);

  // ✅ POLLING “quase realtime”
  React.useEffect(() => {
    if (!publicToken) return;

    const POLL_MS = 7000;

    let disposed = false;
    let timer: any = null;

    const firstLoad = async () => {
      setLoading(true);
      try {
        await fetchCase();
      } catch (e: any) {
        if (!disposed) setError(e?.message || "Falha ao buscar case");
      } finally {
        if (!disposed) setLoading(false);
      }
    };

    firstLoad();

    timer = setInterval(() => {
      if (disposed) return;
      if (document.visibilityState !== "visible") return;

      fetchCase().catch((e: any) => {
        if (!disposed) setError(e?.message || "Falha ao atualizar case");
      });
    }, POLL_MS);

    const onVis = () => {
      if (document.visibilityState === "visible") {
        fetchCase().catch(() => null);
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      disposed = true;
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [publicToken, fetchCase]);

  // ✅ fallback se abrir a página sem param
  if (!publicToken)
    return <Navigate to="/minhas-solicitacoes/laboratorio-cases" replace />;

  const equipamentoNome = loading
    ? "Carregando..."
    : error
    ? "—"
    : safeText(reparo?.equipamento_nome);

  const caseRef = loading
    ? "Carregando..."
    : error
    ? "—"
    : safeText(reparo?.case_ref);

  const solicitanteNome = loading
    ? "Carregando..."
    : error
    ? "—"
    : safeText((reparo as any)?.solicitante_nome);

  const empresaNome = loading
    ? "Carregando..."
    : error
    ? "—"
    : safeText(reparo?.empresa_cliente_razao_social);

  return (
    <div>
      <TitleButtons
        title={``}
        buttonMarginRight="0px"
        marginLeft={"40px"}
        outerMarginRight={"40px"}
      />

      {statusOptError && (
        <NoticeBanner type="notify">
          Não foi possível carregar opções de status:{" "}
          <strong>{statusOptError}</strong>
        </NoticeBanner>
      )}

      {error && (
        <NoticeBanner type="error">
          Erro ao carregar dados do case: <strong>{error}</strong>
        </NoticeBanner>
      )}

      <div
        style={{
          padding: "24px 40px 0 40px",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
        }}
      >
        <Card title="Identificação">
          <Field label="Número do Case" value={caseRef} />
          <Field label="Nome do Equipamento" value={equipamentoNome} />
        </Card>

        <Card title="Solicitante">
          <Field label="Nome" value={solicitanteNome} />
          <Field label="Empresa" value={empresaNome} />
        </Card>
      </div>

      <div style={{ padding: "16px 40px 0 40px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div
            style={{
              fontFamily: "Inter",
              fontWeight: 800,
              fontSize: 15,
              marginBottom: 10,
            }}
          >
            Rastreamento do Case
          </div>
          <div style={{ fontFamily: "Inter", fontSize: 12, opacity: 0.7 }}>
            {timelineItems.length} evento(s) • Atualiza a cada 7s
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 16, opacity: 0.7, fontSize: 14 }}>
            Carregando rastreamento…
          </div>
        ) : timelineItems.length === 0 ? (
          <div style={{ padding: 16, opacity: 0.7, fontSize: 14 }}>
            Sem histórico para este case.
          </div>
        ) : (
          <div style={{ padding: "0 0 0 0" }}>
            <Timeline items={timelineItems} />
          </div>
        )}

        <div style={{ height: 24 }} />
      </div>
    </div>
  );
};

export default LaboratorioDetalhesCaseTimeline;
